import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';

const DashboardContainer = styled.div`
  display: flex;
  min-height: 100vh;
  background: linear-gradient(135deg, #0c162d 0%, #122240 50%, #0a192f 100%);
  color: #c4cdda;
`;

const Sidebar = styled.div`
  width: 250px;
  background: rgba(18, 34, 64, 0.8);
  border-right: 1px solid rgba(0, 194, 255, 0.2);
  padding: 20px 0;
  box-shadow: 3px 0 15px rgba(0, 0, 0, 0.3);
  
  @media (max-width: 768px) {
    width: 50px;
  }
`;

const SidebarHeader = styled.div`
  padding: 0 20px 20px;
  border-bottom: 1px solid rgba(0, 194, 255, 0.2);
  margin-bottom: 20px;
  
  h2 {
    color: #64ffda;
    margin: 0;
    font-size: 1.5rem;
  }
`;

const SidebarMenu = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const MenuItem = styled.li<{ active?: boolean }>`
  padding: 15px 20px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  border-left: 3px solid ${props => props.active ? '#00c2ff' : 'transparent'};
  
  &:hover {
    background: rgba(0, 194, 255, 0.1);
  }
  
  ${props => props.active && `
    background: rgba(0, 194, 255, 0.1);
  `}
  
  span {
    margin-left: 10px;
    
    @media (max-width: 768px) {
      display: none;
    }
  }
  
  @media (max-width: 768px) {
    justify-content: center;
    padding: 15px 0;
  }
`;

const MainContent = styled.div`
  flex: 1;
  padding: 20px;
  overflow-y: auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  padding-bottom: 20px;
  border-bottom: 1px solid rgba(0, 194, 255, 0.2);
`;

const WelcomeText = styled.div`
  h1 {
    margin: 0;
    color: #ffffff;
    font-size: 2rem;
  }
  
  p {
    margin: 5px 0 0;
    color: #8892b0;
  }
`;

const LogoutButton = styled.button`
  background: rgba(255, 107, 107, 0.2);
  color: #ff6b6b;
  border: 1px solid rgba(255, 107, 107, 0.5);
  padding: 10px 20px;
  border-radius: 30px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-weight: 500;
  
  &:hover {
    background: rgba(255, 107, 107, 0.3);
    transform: translateY(-2px);
  }
`;

const DashboardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 25px;
  margin-bottom: 30px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
`;

const DashboardCard = styled.div`
  background: rgba(18, 34, 64, 0.6);
  border-radius: 15px;
  padding: 25px;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(0, 194, 255, 0.2);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 12px 35px rgba(0, 194, 255, 0.4);
    border-color: rgba(0, 194, 255, 0.5);
  }
  
  h3 {
    margin-top: 0;
    color: #64ffda;
    display: flex;
    align-items: center;
    
    span {
      margin-right: 10px;
      font-size: 1.5rem;
    }
  }
`;

const SectionContent = styled.div`
  h2 {
    color: #ffffff;
    margin-top: 0;
  }
  
  p {
    line-height: 1.6;
  }
`;

const PortfolioGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 20px;
  margin-top: 20px;
`;

const PortfolioCard = styled.div`
  background: rgba(18, 34, 64, 0.4);
  border-radius: 10px;
  padding: 20px;
  border: 1px solid rgba(0, 194, 255, 0.1);
  
  h4 {
    margin: 0 0 10px 0;
    color: #64ffda;
  }
  
  .value {
    font-size: 1.5rem;
    font-weight: bold;
    color: #ffffff;
    margin: 10px 0;
  }
  
  .change {
    color: ${props => props.color === 'positive' ? '#64ffda' : '#ff6b6b'};
    font-weight: 500;
  }
`;

const TransactionList = styled.ul`
  list-style: none;
  padding: 0;
  
  li {
    padding: 15px 0;
    border-bottom: 1px solid rgba(255, 255, 255, 0.05);
    
    &:last-child {
      border-bottom: none;
    }
    
    display: flex;
    justify-content: space-between;
  }
`;

const BrokerGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 15px;
`;

const BrokerCard = styled.div`
  background: rgba(18, 34, 64, 0.4);
  border-radius: 10px;
  padding: 15px;
  text-align: center;
  border: 1px solid rgba(0, 194, 255, 0.1);
  
  .logo {
    width: 50px;
    height: 50px;
    background: linear-gradient(135deg, #00c2ff, #64ffda);
    border-radius: 50%;
    margin: 0 auto 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: bold;
    color: #0c162d;
  }
  
  .name {
    font-weight: 500;
    color: #ffffff;
  }
  
  .status {
    font-size: 0.8rem;
    color: #64ffda;
    margin-top: 5px;
  }
`;

const NewsItem = styled.div`
  padding: 15px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  
  &:last-child {
    border-bottom: none;
  }
  
  h4 {
    margin: 0 0 5px 0;
    color: #64ffda;
  }
  
  .date {
    font-size: 0.8rem;
    color: #8892b0;
    margin-bottom: 10px;
  }
  
  p {
    margin: 0;
    font-size: 0.9rem;
  }
`;

const SettingsForm = styled.form`
  background: rgba(18, 34, 64, 0.4);
  border-radius: 10px;
  padding: 20px;
  border: 1px solid rgba(0, 194, 255, 0.1);
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
  
  label {
    display: block;
    margin-bottom: 8px;
    color: #c4cdda;
    font-weight: 500;
  }
  
  input, select {
    width: 100%;
    padding: 12px 15px;
    border-radius: 8px;
    border: 1px solid rgba(0, 194, 255, 0.3);
    background: rgba(18, 34, 64, 0.6);
    color: #ffffff;
    font-size: 1rem;
    
    &:focus {
      outline: none;
      border-color: #00c2ff;
      box-shadow: 0 0 0 2px rgba(0, 194, 255, 0.2);
    }
  }
`;

const SaveButton = styled.button`
  background: linear-gradient(90deg, #00c2ff, #64ffda);
  color: #0c162d;
  border: none;
  padding: 12px 30px;
  border-radius: 30px;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(0, 194, 255, 0.4);
  }
`;

const Dashboard = () => {
  const [activeSection, setActiveSection] = useState('overview');
  const navigate = useNavigate();

  // Check if user is authenticated
  useEffect(() => {
    const isAuthenticated = localStorage.getItem('isAuthenticated') === 'true';
    if (!isAuthenticated) {
      // Redirect to login page if not authenticated
      navigate('/login');
    }
  }, [navigate]);

  const handleLogout = () => {
    // Clear authentication state
    localStorage.removeItem('isAuthenticated');
    // Redirect to home page
    navigate('/');
  };

  const menuItems = [
    { id: 'overview', label: 'Overview', icon: '📊' },
    { id: 'portfolio', label: 'Portfolio', icon: '💼' },
    { id: 'brokers', label: 'Brokers', icon: '🏦' },
    { id: 'signals', label: 'Signals', icon: '📈' },
    { id: 'news', label: 'News', icon: '📰' },
    { id: 'platform', label: 'Platform', icon: '💻' },
    { id: 'transactions', label: 'Transactions', icon: '💸' },
    { id: 'referral', label: 'Referral Program', icon: '👥' },
    { id: 'settings', label: 'Settings', icon: '⚙️' },
    { id: 'support', label: 'Support', icon: '🎧' },
  ];

  const renderSection = () => {
    switch (activeSection) {
      case 'overview':
        return (
          <SectionContent>
            <h2>Dashboard Overview</h2>
            <DashboardGrid>
              <DashboardCard>
                <h3><span>💰</span> Portfolio Value</h3>
                <div className="value">$24,568.75</div>
                <div className="change">+2.35% today</div>
              </DashboardCard>
              
              <DashboardCard>
                <h3><span>📊</span> Active Positions</h3>
                <div className="value">12</div>
                <div className="change">3 new today</div>
              </DashboardCard>
              
              <DashboardCard>
                <h3><span>📈</span> Best Performer</h3>
                <div className="value">AAPL</div>
                <div className="change">+5.21%</div>
              </DashboardCard>
            </DashboardGrid>
            
            <DashboardCard>
              <h3><span>🔔</span> Recent Activity</h3>
              <ul>
                <li>Bitcoin position opened - $2,350.00</li>
                <li>Ethereum signal received - Strong Buy</li>
                <li>Portfolio rebalanced automatically</li>
                <li>New broker connection established</li>
              </ul>
            </DashboardCard>
          </SectionContent>
        );
      
      case 'portfolio':
        return (
          <SectionContent>
            <h2>Portfolio Management</h2>
            <DashboardGrid>
              <DashboardCard>
                <h3><span>💼</span> Asset Allocation</h3>
                <PortfolioGrid>
                  <PortfolioCard color="positive">
                    <h4>Stocks</h4>
                    <div className="value">45%</div>
                    <div className="change">+1.2%</div>
                  </PortfolioCard>
                  
                  <PortfolioCard color="positive">
                    <h4>Crypto</h4>
                    <div className="value">30%</div>
                    <div className="change">+3.5%</div>
                  </PortfolioCard>
                  
                  <PortfolioCard color="negative">
                    <h4>Commodities</h4>
                    <div className="value">15%</div>
                    <div className="change">-0.8%</div>
                  </PortfolioCard>
                  
                  <PortfolioCard color="positive">
                    <h4>Forex</h4>
                    <div className="value">10%</div>
                    <div className="change">+0.5%</div>
                  </PortfolioCard>
                </PortfolioGrid>
              </DashboardCard>
              
              <DashboardCard>
                <h3><span>📈</span> Performance</h3>
                <div className="value">$24,568.75</div>
                <div className="change">+2.35% (+$560.25)</div>
                <p style={{ marginTop: '15px' }}>
                  Your portfolio is performing above average compared to similar profiles.
                </p>
              </DashboardCard>
            </DashboardGrid>
          </SectionContent>
        );
      
      case 'brokers':
        return (
          <SectionContent>
            <h2>Connected Brokers</h2>
            <DashboardGrid>
              <DashboardCard>
                <h3><span>🏦</span> Active Brokers</h3>
                <BrokerGrid>
                  <BrokerCard>
                    <div className="logo">E</div>
                    <div className="name">Exness</div>
                    <div className="status">Connected</div>
                  </BrokerCard>
                  
                  <BrokerCard>
                    <div className="logo">X</div>
                    <div className="name">XM</div>
                    <div className="status">Connected</div>
                  </BrokerCard>
                  
                  <BrokerCard>
                    <div className="logo">I</div>
                    <div className="name">IC Markets</div>
                    <div className="status">Connected</div>
                  </BrokerCard>
                </BrokerGrid>
              </DashboardCard>
              
              <DashboardCard>
                <h3><span>➕</span> Add New Broker</h3>
                <p>Connect to additional brokers to expand your trading options.</p>
                <button style={{ 
                  marginTop: '15px',
                  background: 'linear-gradient(90deg, #00c2ff, #64ffda)',
                  color: '#0c162d',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '30px',
                  cursor: 'pointer',
                  fontWeight: '600'
                }}>
                  Connect Broker
                </button>
              </DashboardCard>
            </DashboardGrid>
          </SectionContent>
        );
      
      case 'signals':
        return (
          <SectionContent>
            <h2>Trading Signals</h2>
            <DashboardGrid>
              <DashboardCard>
                <h3><span>📈</span> Active Signals</h3>
                <ul>
                  <li><strong>Strong Buy:</strong> BTC/USD - Entry: $42,500</li>
                  <li><strong>Sell:</strong> EUR/USD - Resistance at 1.0850</li>
                  <li><strong>Buy:</strong> AAPL - Support at $170.25</li>
                  <li><strong>Neutral:</strong> TSLA - Waiting for breakout</li>
                </ul>
              </DashboardCard>
              
              <DashboardCard>
                <h3><span>🔔</span> Signal History</h3>
                <ul>
                  <li>ETH/USD Buy - <span style={{ color: '#64ffda' }}>+4.2%</span> profit</li>
                  <li>NVDA Sell - <span style={{ color: '#64ffda' }}>+2.8%</span> profit</li>
                  <li>AMZN Buy - <span style={{ color: '#ff6b6b' }}>-1.5%</span> loss</li>
                </ul>
              </DashboardCard>
            </DashboardGrid>
          </SectionContent>
        );
      
      case 'news':
        return (
          <SectionContent>
            <h2>Market News</h2>
            <DashboardGrid>
              <DashboardCard>
                <h3><span>📰</span> Latest Updates</h3>
                <NewsItem>
                  <h4>Fed Holds Rates Steady Amid Inflation Concerns</h4>
                  <div className="date">2 hours ago</div>
                  <p>The Federal Reserve maintained interest rates as policymakers weigh inflation data against economic growth signals.</p>
                </NewsItem>
                
                <NewsItem>
                  <h4>Bitcoin Surges Past $42,000 on Institutional Demand</h4>
                  <div className="date">4 hours ago</div>
                  <p>Cryptocurrency prices jumped as several major companies announced Bitcoin purchases and integration plans.</p>
                </NewsItem>
                
                <NewsItem>
                  <h4>Tech Stocks Rally Ahead of Earnings Season</h4>
                  <div className="date">6 hours ago</div>
                  <p>Nasdaq climbs 1.8% as investors anticipate strong quarterly results from major technology firms.</p>
                </NewsItem>
              </DashboardCard>
            </DashboardGrid>
          </SectionContent>
        );
      
      case 'platform':
        return (
          <SectionContent>
            <h2>Trading Platform</h2>
            <DashboardGrid>
              <DashboardCard>
                <h3><span>💻</span> Platform Access</h3>
                <p>Access your trading platform directly from UPM Traders dashboard.</p>
                <button style={{ 
                  marginTop: '15px',
                  background: 'linear-gradient(90deg, #00c2ff, #64ffda)',
                  color: '#0c162d',
                  border: 'none',
                  padding: '12px 25px',
                  borderRadius: '30px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  fontSize: '1rem'
                }}>
                  Launch Trading Platform
                </button>
              </DashboardCard>
              
              <DashboardCard>
                <h3><span>📱</span> Mobile App</h3>
                <p>Download our mobile app for trading on the go.</p>
                <div style={{ display: 'flex', gap: '10px', marginTop: '15px' }}>
                  <button style={{ 
                    background: '#000',
                    color: '#fff',
                    border: 'none',
                    padding: '10px 15px',
                    borderRadius: '8px',
                    cursor: 'pointer'
                  }}>
                    App Store
                  </button>
                  <button style={{ 
                    background: '#34a853',
                    color: '#fff',
                    border: 'none',
                    padding: '10px 15px',
                    borderRadius: '8px',
                    cursor: 'pointer'
                  }}>
                    Google Play
                  </button>
                </div>
              </DashboardCard>
            </DashboardGrid>
          </SectionContent>
        );
      
      case 'transactions':
        return (
          <SectionContent>
            <h2>Transaction History</h2>
            <DashboardCard>
              <h3><span>💸</span> Recent Transactions</h3>
              <TransactionList>
                <li>
                  <div>Deposit - Bank Transfer</div>
                  <div style={{ color: '#64ffda' }}>+$5,000.00</div>
                </li>
                <li>
                  <div>Withdrawal - Crypto</div>
                  <div style={{ color: '#ff6b6b' }}>−$1,250.00</div>
                </li>
                <li>
                  <div>Trade - BTC/USD</div>
                  <div style={{ color: '#64ffda' }}>+$345.75</div>
                </li>
                <li>
                  <div>Subscription Fee</div>
                  <div style={{ color: '#ff6b6b' }}>−$29.99</div>
                </li>
                <li>
                  <div>Dividend - AAPL</div>
                  <div style={{ color: '#64ffda' }}>+$42.50</div>
                </li>
              </TransactionList>
            </DashboardCard>
          </SectionContent>
        );
      
      case 'referral':
        return (
          <SectionContent>
            <h2>Referral Program</h2>
            <DashboardGrid>
              <DashboardCard>
                <h3><span>👥</span> Invite Friends</h3>
                <p>Earn rewards for every friend you refer who signs up and trades.</p>
                <div style={{ 
                  background: 'rgba(0, 194, 255, 0.1)',
                  padding: '15px',
                  borderRadius: '8px',
                  margin: '15px 0',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '1.2rem', fontWeight: 'bold', color: '#64ffda' }}>
                    Your Referral Code: UPM12345
                  </div>
                </div>
                <button style={{ 
                  width: '100%',
                  background: 'linear-gradient(90deg, #00c2ff, #64ffda)',
                  color: '#0c162d',
                  border: 'none',
                  padding: '12px',
                  borderRadius: '30px',
                  cursor: 'pointer',
                  fontWeight: '600'
                }}>
                  Copy Referral Link
                </button>
              </DashboardCard>
              
              <DashboardCard>
                <h3><span>🏆</span> Referral Rewards</h3>
                <ul>
                  <li><strong>$50</strong> for each referred user</li>
                  <li><strong>5% cashback</strong> on their first month</li>
                  <li><strong>Exclusive bonuses</strong> for top referrers</li>
                </ul>
                <div style={{ 
                  marginTop: '15px',
                  padding: '15px',
                  background: 'rgba(100, 255, 218, 0.1)',
                  borderRadius: '8px'
                }}>
                  <div style={{ fontWeight: 'bold', color: '#64ffda' }}>Total Earned: $250.00</div>
                  <div>5 successful referrals</div>
                </div>
              </DashboardCard>
            </DashboardGrid>
          </SectionContent>
        );
      
      case 'settings':
        return (
          <SectionContent>
            <h2>Account Settings</h2>
            <DashboardGrid>
              <DashboardCard>
                <h3><span>👤</span> Profile Information</h3>
                <SettingsForm>
                  <FormGroup>
                    <label htmlFor="name">Full Name</label>
                    <input type="text" id="name" defaultValue="John Doe" />
                  </FormGroup>
                  
                  <FormGroup>
                    <label htmlFor="email">Email Address</label>
                    <input type="email" id="email" defaultValue="john.doe@example.com" />
                  </FormGroup>
                  
                  <SaveButton>Save Changes</SaveButton>
                </SettingsForm>
              </DashboardCard>
              
              <DashboardCard>
                <h3><span>🔒</span> Security Settings</h3>
                <SettingsForm>
                  <FormGroup>
                    <label htmlFor="password">New Password</label>
                    <input type="password" id="password" placeholder="Enter new password" />
                  </FormGroup>
                  
                  <FormGroup>
                    <label htmlFor="confirmPassword">Confirm Password</label>
                    <input type="password" id="confirmPassword" placeholder="Confirm new password" />
                  </FormGroup>
                  
                  <SaveButton>Update Password</SaveButton>
                </SettingsForm>
              </DashboardCard>
              
              <DashboardCard>
                <h3><span>🔔</span> Notification Preferences</h3>
                <SettingsForm>
                  <FormGroup>
                    <label htmlFor="email-notifications">
                      <input type="checkbox" id="email-notifications" defaultChecked /> Email Notifications
                    </label>
                  </FormGroup>
                  
                  <FormGroup>
                    <label htmlFor="sms-notifications">
                      <input type="checkbox" id="sms-notifications" /> SMS Notifications
                    </label>
                  </FormGroup>
                  
                  <FormGroup>
                    <label htmlFor="trade-alerts">
                      <input type="checkbox" id="trade-alerts" defaultChecked /> Trade Alerts
                    </label>
                  </FormGroup>
                  
                  <SaveButton>Save Preferences</SaveButton>
                </SettingsForm>
              </DashboardCard>
            </DashboardGrid>
          </SectionContent>
        );
      
      case 'support':
        return (
          <SectionContent>
            <h2>Customer Support</h2>
            <DashboardGrid>
              <DashboardCard>
                <h3><span>🎫</span> Submit a Ticket</h3>
                <SettingsForm>
                  <FormGroup>
                    <label htmlFor="subject">Subject</label>
                    <input type="text" id="subject" placeholder="Brief description of your issue" />
                  </FormGroup>
                  
                  <FormGroup>
                    <label htmlFor="category">Category</label>
                    <select id="category">
                      <option>Account Issues</option>
                      <option>Technical Support</option>
                      <option>Billing Questions</option>
                      <option>Trading Assistance</option>
                      <option>General Inquiry</option>
                    </select>
                  </FormGroup>
                  
                  <FormGroup>
                    <label htmlFor="message">Message</label>
                    <textarea 
                      id="message" 
                      placeholder="Please describe your issue in detail" 
                      style={{ 
                        width: '100%', 
                        minHeight: '120px', 
                        padding: '12px 15px',
                        borderRadius: '8px',
                        border: '1px solid rgba(0, 194, 255, 0.3)',
                        background: 'rgba(18, 34, 64, 0.6)',
                        color: '#ffffff',
                        fontSize: '1rem',
                        fontFamily: 'inherit'
                      }}
                    />
                  </FormGroup>
                  
                  <SaveButton>Submit Ticket</SaveButton>
                </SettingsForm>
              </DashboardCard>
              
              <DashboardCard>
                <h3><span>📞</span> Contact Information</h3>
                <ul>
                  <li><strong>Support Email:</strong> support@upmtraders.com</li>
                  <li><strong>Phone:</strong> +1 (800) 555-1234</li>
                  <li><strong>Live Chat:</strong> Available 24/7</li>
                  <li><strong>Response Time:</strong> Within 2 hours</li>
                </ul>
                
                <div style={{ 
                  marginTop: '20px',
                  padding: '15px',
                  background: 'rgba(0, 194, 255, 0.1)',
                  borderRadius: '8px'
                }}>
                  <div style={{ fontWeight: 'bold', color: '#64ffda' }}>Emergency Support</div>
                  <div>For urgent issues, call: +1 (800) 555-9999</div>
                </div>
              </DashboardCard>
            </DashboardGrid>
          </SectionContent>
        );
      
      default:
        return (
          <SectionContent>
            <h2>Welcome to UPM Traders Dashboard</h2>
            <p>Select a section from the sidebar to get started.</p>
          </SectionContent>
        );
    }
  };

  return (
    <DashboardContainer>
      <Sidebar>
        <SidebarHeader>
          <h2>UPM Traders</h2>
        </SidebarHeader>
        <SidebarMenu>
          {menuItems.map((item) => (
            <MenuItem 
              key={item.id} 
              active={activeSection === item.id}
              onClick={() => setActiveSection(item.id)}
            >
              <span>{item.icon}</span>
              <span>{item.label}</span>
            </MenuItem>
          ))}
          <MenuItem onClick={handleLogout}>
            <span>🚪</span>
            <span>Logout</span>
          </MenuItem>
        </SidebarMenu>
      </Sidebar>
      
      <MainContent>
        <Header>
          <WelcomeText>
            <h1>Client Dashboard</h1>
            <p>Welcome back, John Doe</p>
          </WelcomeText>
          <LogoutButton onClick={handleLogout}>Logout</LogoutButton>
        </Header>
        
        {renderSection()}
      </MainContent>
    </DashboardContainer>
  );
};

export default Dashboard;